import { Service, computed, inject, signal } from '@angular/core';
import { SimEngineService } from './sim-engine.service';

/**
 * The standing fault a throw leaves behind, as the Subscriber's `error` column reported it. It has to
 * outlive the tick that raised it because the teardown it caused does too: the board sits dead until
 * the player either fixes what fed the throw or subscribes again, and until then the cause stays on
 * screen.
 *
 * Only a *reported* failure gets here. With nothing installed in `error` the notification has nowhere
 * to land, so the board dies in silence and the rx-log's uncaught-error line is all the player gets —
 * which is what makes installing a handler worth doing, and what an unhandled RxJS error really is.
 */
@Service()
export class SubscriptionErrorService {
  private readonly engine = inject(SimEngineService);
  private readonly messageSignal = signal<string | null>(null);
  private readonly dismissed = signal(false);

  readonly message = this.messageSignal.asReadonly();
  readonly bannerVisible = computed(() => this.messageSignal() !== null && !this.dismissed());

  constructor() {
    this.engine.events.subscribe((events) => {
      for (const event of events) {
        if (event.type === 'subscriptionErrored' && event.report !== null) this.raise(event.report);
      }
    });
    this.engine.subscriptionChanged.subscribe(({ subscribed }) => {
      if (subscribed) this.clear();
    });
    this.engine.layoutChanged.subscribe(() => this.clear());
  }

  dismiss(): void {
    this.dismissed.set(true);
  }

  private raise(message: string): void {
    this.messageSignal.set(message);
    this.dismissed.set(false);
  }

  private clear(): void {
    this.messageSignal.set(null);
    this.dismissed.set(false);
  }
}
